import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router";
import {
  getClientById,
  editClient,
  getAllIdTypes,
  getAllCountries,
  getAllSexTypes,
  getAllStatesByCountry,
  getTicketsByClientId,
  getClientStatsByClientId,
  getClientMovementsById,
  addMovementToClient,
  getPointsSummaryByClientId,
  setPointsToClient,
  getPromotionsUsedByClientId,
  getProductsRecommendationsByClientId,
  deleteProductRecommendation,
  getClientLevels,
  getPurchasedItemsByClientId,
} from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import { COMMERCIAL_INTELLIGENCE_PATHS } from "../../helpers/MenuPathHelper";

import {
  Form,
  message,
  Select,
  Button,
  Input,
  Spin,
  DatePicker,
  Switch,
  Table,
  Space,
  Typography,
  Modal,
  InputNumber,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { RxPerson } from "react-icons/rx";

import styles from "./ViewClient.module.scss";
import NotFound from "../notfound/NotFound";
import CustomBreadcrumb from "../../components/breadcrumb/Breadcrumb";
import ViewClientDiagnosisTab from "./ViewClientDiagnosisTab";

const { TabPane } = Tabs;

const getStatesOptions = (states: { id: string | number; name: string }[] = []) =>
  states.map((state: { id: string | number; name: string }) => (
    <Select.Option key={state.id} value={state.id}>
      {state.name}
    </Select.Option>
  ));

const ViewClient = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [idTypes, setIdTypes] = useState<any[]>([]);
  const [sexTypes, setSexTypes] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [statesByCountry, setStatesByCountry] = useState<any[]>([]);
  const [clientLevels, setClientLevels] = useState<any[]>([]);
  const [client, setClient] = useState<any>();
  const [tickets, setTickets] = useState<any[]>([]);
  const [actualPageTickets, setActualPageTickets] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [fidelity, setFidelity] = useState("test");
  const [clientStats, setClientStats] = useState<null | {
    lastPurchaseDate: string;
    daysSinceLastPurchase: number;
    averageSpend: number;
    totalSpend: number;
    purchaseCount: number;
    returnCount: number;
    totalReturns: number;
  }>();
  const [clientMovements, setClientMovements] = useState<any[]>([]);
  const [loadingMovements, setLoadingMovements] = useState(true);
  const [pointsSummary, setPointsSummary] = useState<{
    activePoints: number;
    pendingPoints: number;
    spentPoints: number;
    expiredPoints: number;
    totalGeneratedPoints: number;
    futureExpirations: number;
    pointsExpirationDays: number | null;
    nextExpiration: {
      points: number;
      expiresAt: string;
      daysUntilExpiration: number;
    } | null;
    expiringSoon: {
      points: number;
      expiresAt: string;
      daysUntilExpiration: number;
    }[];
  } | null>(null);
  const [isCreateMovementModalVisible, setIsCreateMovementModalVisible] =
    useState(false);
  const [isEditUnitsModalVisible, setIsEditUnitsModalVisible] = useState(false);
  const [totalSizeMovements, setTotalSizeMovements] = useState(0);
  const [actualPageMovements, setActualPageMovements] = useState(1);

  const [promotionsUsed, setPromotionsUsed] = useState<any[]>([]);
  const [actualPagePromotionsUsed, setActualPagePromotionsUsed] = useState(1);
  const [totalPromotionsUsed, setTotalPromotionsUsed] = useState(0);
  const [loadingPromotionsUsed, setLoadingPromotionsUsed] = useState(true);

  const [productsRecommendations, setProductsRecommendations] = useState<any[]>(
    []
  );
  const [
    actualPageProductsRecommendations,
    setActualPageProductsRecommendations,
  ] = useState(1);
  const [totalProductsRecommendations, setTotalProductsRecommendations] =
    useState(0);
  const [loadingProductsRecommendations, setLoadingProductsRecommendations] =
    useState(true);
  const [isCreateRecommendationVisible, setIsCreateRecommendationModalVisible] =
    useState(false);

  // Purchased items states
  const [purchasedSkus, setPurchasedSkus] = useState<any[]>([]);
  const [actualPagePurchasedSkus, setActualPagePurchasedSkus] = useState(1);
  const [totalPurchasedSkus, setTotalPurchasedSkus] = useState(0);
  const [loadingPurchasedSkus, setLoadingPurchasedSkus] = useState(true);

  const [purchasedBrands, setPurchasedBrands] = useState<any[]>([]);
  const [actualPagePurchasedBrands, setActualPagePurchasedBrands] = useState(1);
  const [totalPurchasedBrands, setTotalPurchasedBrands] = useState(0);
  const [loadingPurchasedBrands, setLoadingPurchasedBrands] = useState(true);

  const [purchasedCategories, setPurchasedCategories] = useState<any[]>([]);
  const [actualPagePurchasedCategories, setActualPagePurchasedCategories] = useState(1);
  const [totalPurchasedCategories, setTotalPurchasedCategories] = useState(0);
  const [loadingPurchasedCategories, setLoadingPurchasedCategories] = useState(true);

  const [infoForm] = Form.useForm();
  const [addMovementForm] = Form.useForm();
  const [unitsForm] = Form.useForm();

  const canEdit = hasPermission(privilegesEnum.WRITE_CLIENTS);

  const { id } = useParams();

  const renderValidationStatus = (validated?: boolean | null) => {
    if (validated === true) {
      return (
        <Tag className={styles.contactValidationTag} color="success">
          {t("Common-ValidationStatusValid")}
        </Tag>
      );
    }
    if (validated === false) {
      return (
        <Tag className={styles.contactValidationTag} color="error">
          {t("Common-ValidationStatusInvalid")}
        </Tag>
      );
    }
    return (
      <Tag className={styles.contactValidationTag}>
        {t("Common-ValidationStatusNotChecked")}
      </Tag>
    );
  };

  const renderContactLabel = (label: string, validated?: boolean | null) => {
    return (
      <span className={styles.contactValidationLabel}>
        <span>{label}</span>
        {renderValidationStatus(validated)}
      </span>
    );
  };

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENTS)) {
      handleFetchClient();
      handleFetchProductsRecommendations(actualPageProductsRecommendations);
      handleFetchCountries();
      handleFetchIdTypes();
      handleFetchSexTypes();
      handleFetchClientLevels();
    }
  }, []);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENTS)) {
      handleFetchTickets(actualPageTickets);
      handleFetchPromotionsUsed(actualPagePromotionsUsed);
      handleFetchStats();
      handleFetchPurchasedItems(actualPagePurchasedSkus);
    }
  }, [actualPageTickets]);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENTS)) {
      handleFetchClientMovements(actualPageMovements);
    }
  }, [actualPageMovements]);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENTS)) {
      handleFetchPointsSummary();
    }
  }, []);

  const handleFetchPointsSummary = async () => {
    if (id) {
      const response = await getPointsSummaryByClientId(parseInt(id));
      setPointsSummary(response);
    }
  };

  const handleFetchClient = async () => {
    setLoading(true);

    let response = await getClientById(id!);
    setClient(response);
    if (response.fidelity) setFidelity(response.fidelity);
    if (response.country?.id) {
      await handleFetchStatesByCountry(response.country.id);
    }
    setLoading(false);
  };

  const handleFetchClientMovements = async (page: number) => {
    setLoadingMovements(true);
    let response = await getClientMovementsById(id!, actualPageMovements);
    setClientMovements(response.points);
    setLoadingMovements(false);
    setActualPageMovements(page);
    setTotalSizeMovements(response.totalElements);
  };
  const handleFetchStats = async () => {
    if (id) {
      const response = await getClientStatsByClientId(id);
      setClientStats(response);
    }
  };

  const handleFetchTickets = async (page: number) => {
    if (id) {
      setLoadingTickets(true);
      const response = await getTicketsByClientId(page, id);
      setTickets(response.tickets);
      setTotalTickets(response.totalElements);
      setActualPageTickets(page);
      setLoadingTickets(false);
    }
  };

  const handleFetchProductsRecommendations = async (page: number) => {
    setLoadingProductsRecommendations(true);
    const response = await getProductsRecommendationsByClientId(page, id!);
    setProductsRecommendations(response.recommendations);
    setTotalProductsRecommendations(response.totalElements);
    setActualPageProductsRecommendations(page);
    setLoadingProductsRecommendations(false);
  };

  const handleFetchPromotionsUsed = async (page: number) => {
    if (id) {
      setLoadingPromotionsUsed(true);
      const response = await getPromotionsUsedByClientId(page, id);
      setPromotionsUsed(response.promotions);
      setTotalPromotionsUsed(response.totalElements);
      setActualPagePromotionsUsed(page);
      setLoadingPromotionsUsed(false);
    }
  };

  const handleFetchPurchasedItems = async (page: number) => {
    if (id) {
      setLoadingPurchasedSkus(true);
      setLoadingPurchasedBrands(true);
      setLoadingPurchasedCategories(true);
      
      const response = await getPurchasedItemsByClientId(page, id);
      
      setPurchasedSkus(response.purchasedSkus);
      setTotalPurchasedSkus(response.totalSkus);
      setActualPagePurchasedSkus(page);
      
      setPurchasedBrands(response.purchasedBrands);
      setTotalPurchasedBrands(response.totalBrands);
      setActualPagePurchasedBrands(page);
      
      setPurchasedCategories(response.purchasedCategories);
      setTotalPurchasedCategories(response.totalCategories);
      setActualPagePurchasedCategories(page);
      
      setLoadingPurchasedSkus(false);
      setLoadingPurchasedBrands(false);
      setLoadingPurchasedCategories(false);
    }
  };

  const handleFetchIdTypes = async () => {
    const response = await getAllIdTypes();
    setIdTypes(response);
  };

  const handleFetchCountries = async () => {
    const response = await getAllCountries();
    setCountries(response);
  };

  const handleFetchSexTypes = async () => {
    const response = await getAllSexTypes();
    setSexTypes(response);
  };

  const handleFetchClientLevels = async () => {
    const { clientLevels } = await getClientLevels(1, {});
    setClientLevels(clientLevels);
  };

  const getIdTypesOptions = (items: any) => {
    const options: any[] = [];

    items.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.descriptionEs}
        </Select.Option>
      );
    });
    return options;
  };

  const getCountriesOptions = (items: any) => {
    const options: any[] = [];

    items.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });
    return options;
  };

  const handleFetchStatesByCountry = async (countryId: number) => {
    const response = await getAllStatesByCountry(countryId);
    setStatesByCountry(response);
  };

  const getSexTypesOptions = (items: any) => {
    const options: any[] = [];

    items.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.value} key={item.value}>
          {item.description_es}
        </Select.Option>
      );
    });

    return options;
  };

  const getClientLevelsOptions = (items: any) => {
    const options: any[] = [];

    items.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const handleAddMovement = async () => {
    await addMovementForm.validateFields().then(async (movement) => {
      const payload: {
        type: string;
        pointsMoved: number;
        comments: string;
        expirationDate?: string;
      } = {
        type: movement.type,
        pointsMoved: movement.pointsMoved,
        comments: movement.comments,
      };
      if (movement.expirationDate) {
        payload.expirationDate = movement.expirationDate.format("YYYY-MM-DD");
      }

      const response = await addMovementToClient(parseInt(id!), payload);

      if (response) {
        addMovementForm.resetFields();
        await handleFetchClientMovements(1);
        await handleFetchPointsSummary();
        const updatedClient = await getClientById(id!);
        setClient(updatedClient);
        setIsCreateMovementModalVisible(false);
        message.success(t("Common-AddSuccessMessage"));
      } else message.error(t("Common-ErrorMessage"));
    });
  };

  const handleEditUnits = async () => {
    await unitsForm.validateFields().then(async (units) => {
      const response = await setPointsToClient(parseInt(id!), units);
      if (response) {
        unitsForm.resetFields();
        setIsEditUnitsModalVisible(false);
        // Solo actualizar los datos del cliente sin recargar toda la página
        const updatedClient = await getClientById(id!);
        setClient(updatedClient);
        message.success(t("Common-EditSuccessMessage"));
      } else message.error(t("Common-ErrorMessage"));
    });
  };

  const handleDeleteProductRecommendation = async (
    recommendationId: number
  ) => {
    setLoading(true);
    const deleteStatus = await deleteProductRecommendation(
      id!,
      recommendationId
    );
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchProductsRecommendations(actualPageProductsRecommendations);
    setLoading(false);
  };

  const showDeleteRecommendationConfirm = (recommendationId: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDeleteProductRecommendation(recommendationId);
      },
    });
  };


  const handleCancelButton = () => {
    const search = location.search;
    navigate(`/promotions/clients${search}`);
  };

  const handleEdit = async () => {
    setLoading(true);
    const data = infoForm.getFieldsValue();
    data.id = parseInt(id!);
    if (!data.birthdate || !moment(data.birthdate).isValid()) {
      data.birthdate = null;
    } else {
      data.birthdate = moment(data.birthdate).format("YYYY-MM-DD");
    }

    const response = await editClient(data);

    if (response) {
      message.success(t("Common-EditSuccessMessage"));
      handleFetchClient();
    } else message.error(t("Common-EditErrorMessage"));
    setLoading(false);
  };

  const clientSegmentsColumn = [
    {
      title: t("Common-Name"),
      key: "name",
      render: (segment: any) => (
        <a href={`${COMMERCIAL_INTELLIGENCE_PATHS.segments}/${segment.id}/view`}>
          {segment.name}
        </a>
      ),
    },
    {
      title: t("Common-Description"),
      dataIndex: "description",
    },
    {
      title: t("Common-Type"),
      key: "type",
      render: (segment: any) => segment.type.description_es,
    },
  ];

  const promotionsUsedColumns = [
    {
      title: t("Common-Name"),
      render: (promotion: any) => (
        <a href={`${COMMERCIAL_INTELLIGENCE_PATHS.promotions}/${promotion.id}/view`}>
          {promotion.name}
        </a>
      ),
      key: "promotion",
    },
    {
      title: t("Common-Description"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: t("Common-Type"),
      render: (promotion: any) => promotion.type.description_es,
      key: "type",
    },
  ];

  const ticketColumns = [
    {
      title: t("Common-Date"),
      dataIndex: "date",
      key: "date",
      render: (date: any) => moment(date, "YYYY-MM-DD").format("DD-MM-YYYY"),
    },
    {
      title: t("Common-Code"),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("Common-Type"),
      render: (ticket: any) => ticket.documentType.description_es,
      key: "documentType",
    },
    {
      title: t("Common-BranchOffice"),
      render: (ticket: any) => (
        <a
          href={`/admin/branchoffices/${ticket.salePoint.branchOffice?.id}/view`}
        >
          {ticket.salePoint.branchOffice?.name}
        </a>
      ),
      key: "branchOffice",
    },
    {
      title: t("Common-Total"),
      dataIndex: "total",
      key: "total",
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Space size="middle">
          <EyeOutlined
            href={`/admin/tickets/${record.id}/view`}
            onClick={() =>
              navigate(`${COMMERCIAL_INTELLIGENCE_PATHS.tickets}/${record.id}/view`)
            }
          />
        </Space>
      ),
    },
  ];

  const productsRecommendationsColumns = [
    {
      title: t("Common-BuyProbability"),
      dataIndex: "buyProbability",
    },
    {
      title: t("Common-Article"),
      dataIndex: "article",
      render: (article: any) => (
        <a href={`/admin/articles/${article.id}/view`}>{article.name}</a>
      ),
    },
    {
      title: t("Common-Sku"),
      dataIndex: "sku",
      render: (sku: any) => sku.name,
    },
    {
      title: t("Common-Category"),
      key: "name",
      dataIndex: "category",
      render: (category: any) => {
        const parentsString = category?.parents
          ? category.parents.filter(Boolean).join(" - ")
          : "";
        const categoryName = category?.name || "";

        return (
          <a href={`/admin/categories/${category.id}/view`}>
            {parentsString
              ? `${parentsString} - ${categoryName}`
              : categoryName}
          </a>
        );
      },
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Space size="middle">
          {hasPermission(privilegesEnum.DELETE_CLIENTS) && (
            <DeleteOutlined
              onClick={() => {
                showDeleteRecommendationConfirm(record.id);
              }}
            />
          )}
        </Space>
      ),
    },
  ];

  const movementsColumns = [
    {
      title: t("Common-Type"),
      dataIndex: "type",
      render: (type: string) => movementTypeText(type),
    },
    {
      title: t("Common-CreatedDate"),
      dataIndex: "createdAt",
      render: (date: string) => date?.split("T")[0],
    },
    {
      title: t("Common-Points"),
      dataIndex: "pointsMoved",
    },
    {
      title: t("Common-ExpiredDate"),
      dataIndex: "expirationDate",
      render: (date: string, record: any) => {
        if (!date) return <Typography.Text type="secondary">—</Typography.Text>;
        const expired =
          record.expired === true || moment(date).endOf("day").isBefore(moment());
        return (
          <Tag color={expired ? "default" : "blue"}>
            {moment(date).format("DD/MM/YYYY")}
          </Tag>
        );
      },
    },
    {
      title: t("Common-Comments"),
      dataIndex: "comments",
    },
  ];

  const purchasedSkusColumns = [
    {
      title: t("Common-Sku"),
      dataIndex: "skuName",
      key: "skuName",
      render: (skuName: string, record: any) => (
        <a href={`/admin/skus/${record.skuId}/view`}>{skuName}</a>
      ),
    },
    {
      title: t("Common-Article"),
      dataIndex: "articleName",
      key: "articleName",
      render: (articleName: string, record: any) => (
        <a href={`/admin/articles/${record.articleId}/view`}>{articleName}</a>
      ),
    },
    {
      title: t("Common-Category"),
      dataIndex: "categoryName",
      key: "categoryName",
      render: (categoryName: string, record: any) => (
        <a href={`/admin/categories/${record.categoryId}/view`}>{categoryName}</a>
      ),
    },
    {
      title: t("Common-Brand"),
      dataIndex: "brandName",
      key: "brandName",
      render: (brandName: string, record: any) => (
        <a href={`/admin/brands/${record.brandId}/view`}>{brandName}</a>
      ),
    },
    {
      title: t("Common-Quantity"),
      dataIndex: "totalQuantity",
      key: "totalQuantity",
    },
    {
      title: t("Common-Total"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (amount: number) => Math.round(amount || 0),
    },
  ];

  const purchasedBrandsColumns = [
    {
      title: t("Common-Brand"),
      dataIndex: "brandName",
      key: "brandName",
      render: (brandName: string, record: any) => (
        <a href={`/admin/brands/${record.brandId}/view`}>{brandName}</a>
      ),
    },
    {
      title: t("Common-Quantity"),
      dataIndex: "totalQuantity",
      key: "totalQuantity",
    },
    {
      title: t("Common-Total"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (amount: number) => Math.round(amount || 0),
    },
  ];

  const purchasedCategoriesColumns = [
    {
      title: t("Common-Category"),
      dataIndex: "categoryName",
      key: "categoryName",
      render: (categoryName: string, record: any) => (
        <a href={`/admin/categories/${record.categoryId}/view`}>{categoryName}</a>
      ),
    },
    {
      title: t("Common-Quantity"),
      dataIndex: "totalQuantity",
      key: "totalQuantity",
    },
    {
      title: t("Common-Total"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (amount: number) => Math.round(amount || 0),
    },
  ];

  const movementTypeText = (term: string) => {
    const translations: Record<string, string> = {
      ACCUMULATION: t("Common-Accumulation"),
      CONSUMPTION: t("Common-Consumption"),
      ADJUSTMENT: t("Common-Adjustment"),
    };

    return translations[term.toUpperCase()] || "-";
  };

  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : !client ? (
    <NotFound />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxPerson />,
                label: t("Common-Clients"),
                path: "/promotions/clients",
                search: location.search
              },
              {
                label: client?.idNumber || t("Common-Client"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
        <div className={styles.actionButtonsWrapper}>
          {hasPermission(privilegesEnum.WRITE_CLIENTS) && (
            <>
              <Button
                onClick={handleEdit}
                className={styles.saveButton}
                type="primary"
              >
                {t("Common-Save")}
              </Button>
              <Button
                onClick={handleCancelButton}
                className={styles.cancelButton}
                type="primary"
              >
                {t("Common-Cancel")}
              </Button>
            </>
          )}
        </div>
      </div>
      <Tabs defaultActiveKey="personal" className={styles.tabsContainer}>
        <TabPane tab={t("Common-PersonalInfo")} key="personal">
          <div className={`${styles.infoWrapper} ${styles.newForm}`}>
            <Form
              name="basic"
              layout="vertical"
              initialValues={{ remember: true }}
              autoComplete="off"
              form={infoForm}
            >
              <div className={styles.sectionTitle}>{t("Common-PersonalInfo")}</div>
              <div className={styles.itemWrapper}>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-Identifier")}
                  name="documentNumber"
                  initialValue={client?.idNumber && client?.idNumber !== 'NULL' ? client.idNumber : ''}
                  rules={[
                    {
                      required: true,
                      message: t("Common-Identifier"),
                    },
                  ]}
                >
                  <Input placeholder={t("Common-Identifier")} disabled={!canEdit} />
                </Form.Item>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-DocumentType")}
                  name="documentType"
                  initialValue={client?.idType?.id}
                  rules={[
                    {
                      required: true,
                      message: t("Common-DocumentType"),
                    },
                  ]}
                >
                  <Select
                    defaultValue={client?.idType?.id}
                    placeholder={t("Common-DocumentType")}
                    allowClear
                    disabled={!canEdit}
                  >
                    {getIdTypesOptions(idTypes)}
                  </Select>
                </Form.Item>
              </div>
              <div className={styles.itemWrapper}>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-ExternalId")}
                  name="externalId"
                  initialValue={client?.externalId || ''}
                >
                  <Input placeholder={t("Common-ExternalId")} disabled={!canEdit} />
                </Form.Item>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-Sex")}
                  name="sex"
                  initialValue={client?.sex.value}
                >
                  <Select
                    placeholder={t("Common-Sex")}
                    allowClear
                    defaultValue={client?.sex}
                    disabled={!canEdit}
                  >
                    {getSexTypesOptions(sexTypes)}
                  </Select>
                </Form.Item>
              </div>
              <div className={styles.itemWrapper}>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-BirthDate")}
                  name="birthdate"
                  initialValue={client?.birthdate ? moment(client.birthdate, "YYYY-MM-DD") : undefined}
                >
                  <DatePicker
                    className={styles.dateSelector}
                    defaultValue={client?.birthdate ? moment(client.birthdate, "YYYY-MM-DD") : undefined}
                    disabled={!canEdit}
                    format="YYYY-MM-DD"
                    placeholder={t("Common-SelectDate")}
                  />
                </Form.Item>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-Active")}
                  name="active"
                  initialValue={client?.active}
                  rules={[
                    {
                      required: true,
                      message: t("Common-Active"),
                    },
                  ]}
                >
                  <Switch defaultChecked={client?.active} disabled={!canEdit} />
                </Form.Item>
              </div>
              
              <div className={styles.sectionDivider} />
              <div className={styles.sectionTitle}>{t("Common-Location")}</div>
              <div className={styles.itemWrapper}>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-Country")}
                  name="country"
                  initialValue={client?.country?.id}
                >
                  <Select
                    placeholder={t("Common-Country")}
                    defaultValue={client?.country?.id}
                    disabled={!canEdit}
                    showSearch
                    optionFilterProp="children"
                    onChange={(value) => {
                      handleFetchStatesByCountry(value);
                    }}
                  >
                    {getCountriesOptions(countries)}
                  </Select>
                </Form.Item>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-City")}
                  name="city"
                  initialValue={client?.state?.id || ''}
                >
                  {canEdit ? (
                    <Select
                      placeholder={t("Common-City")}
                      defaultValue={client?.state?.id || ''}
                      showSearch
                      optionFilterProp="children"
                    >
                      {getStatesOptions(statesByCountry)}
                    </Select>
                  ) : (
                    <Input disabled value={client?.state?.name || ''} />
                  )}
                </Form.Item>
              </div>
              <div className={styles.itemWrapper}>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-Address")}
                  name="address"
                  initialValue={client?.address || ''}
                >
                  <Input placeholder={t("Common-Address")} disabled={!canEdit} />
                </Form.Item>
                <div className={styles.formItem}></div>
              </div>
              
              <div className={styles.sectionDivider} />
              <div className={styles.sectionTitle}>{t("Common-Contact")}</div>
              <div className={styles.itemWrapper}>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-Name")}
                  name="name"
                  initialValue={client?.name || ''}
                >
                  <Input placeholder={t("Common-Name")} disabled={!canEdit} />
                </Form.Item>
                <Form.Item
                  className={styles.formItem}
                  label={renderContactLabel(t("Common-Phone"), client?.phoneValidated)}
                  name="phone"
                  initialValue={client?.phone || ''}
                >
                  <Input placeholder={t("Common-Phone")} disabled={!canEdit} />
                </Form.Item>
              </div>
              <div className={styles.itemWrapper}>
                <Form.Item
                  className={styles.formItem}
                  label={renderContactLabel(t("Common-Mobile"), client?.mobileValidated)}
                  name="mobile"
                  initialValue={client?.mobile || ''}
                >
                  <Input placeholder={t("Common-Mobile")} disabled={!canEdit} />
                </Form.Item>
                <Form.Item
                  className={styles.formItem}
                  label={renderContactLabel(t("Common-Email"), client?.emailValidated)}
                  name="email"
                  initialValue={client?.email || ''}
                >
                  <Input placeholder={t("Common-Email")} type="email" disabled={!canEdit} />
                </Form.Item>
              </div>
              <div className={styles.itemWrapper}>
                <Form.Item
                  className={styles.formItem}
                  label={
                    <span className={styles.storeValidationLabelRow}>
                      {t("Common-StoreOnlyContactValidation")}
                      <Tooltip
                        title={t("Common-StoreOnlyContactValidationTooltip")}
                      >
                        <InfoCircleOutlined
                          className={styles.storeValidationTooltipIcon}
                          aria-label={t("Common-StoreOnlyContactValidationTooltip")}
                        />
                      </Tooltip>
                    </span>
                  }
                  name="storeOnlyContactValidation"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch disabled={!canEdit} />
                </Form.Item>
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-TermsConditions")}
                  name="termsConditions"
                  valuePropName="checked"
                  initialValue={client?.termsConditions || false}
                >
                  <Switch disabled={!canEdit} />
                </Form.Item>
              </div>
            </Form>
          </div>
        </TabPane>
            
            <TabPane tab={t("Common-Segments")} key="metrics">
              <div className={styles.infoWrapper}>
                <h1 className={styles.subTitle}>{t("Common-Segments")}</h1>
            <div className={styles.newForm}>
              <Form.Item
                className={styles.newItem}
                label={t("Common-Level")}
                name="levelId"
                initialValue={client?.level?.id}
              >
                <Select
                  placeholder={t("Common-Level")}
                  allowClear
                  defaultValue={client?.level?.id}
                  disabled={!canEdit}
                >
                  {getClientLevelsOptions(clientLevels)}
                </Select>
              </Form.Item>
            </div>

            <div className={styles.tableWrapper}>
              <h1 className={styles.subTitle}>{t("Common-Segments")}</h1>

              <Table
                size="small"
                className={styles.table}
                loading={loading}
                rowKey={(record) => record.id}
                columns={clientSegmentsColumn}
                dataSource={client?.segments}
              />
            </div>
              </div>
            </TabPane>
            
            <TabPane tab={t("Common-PurchaseInfo")} key="purchases">
              <div className={styles.infoWrapper}>
                <h1 className={styles.subTitle}>{t("Common-PurchaseInfo")}</h1>
            <div className={styles.statsWrapper}>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-DateLastPurchase")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {clientStats?.lastPurchaseDate ?? "-"}
                </Typography.Text>
              </div>

              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-DaysSinceLastPurchase")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {clientStats?.daysSinceLastPurchase ?? "-"}
                </Typography.Text>
              </div>

              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-SpentAverage")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {clientStats?.averageSpend ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-PurchaseCount")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {clientStats?.purchaseCount ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-TotalSpent")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {clientStats?.totalSpend ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-ReturnCount")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {clientStats?.returnCount ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-TotalReturns")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {clientStats?.totalReturns ?? "-"}
                </Typography.Text>
              </div>
            </div>

            <div className={styles.tableWrapper}>
              <h1 className={styles.subTitle}>{t("Common-Tickets")}</h1>

              <Table
                size="small"
                className={styles.table}
                loading={loadingTickets}
                rowKey={(record) => record.id}
                columns={ticketColumns}
                dataSource={tickets}
                pagination={{
                  total: totalTickets,
                  showSizeChanger: false,
                  onChange: handleFetchTickets,
                  current: actualPageTickets,
                }}
              />
            </div>

            <div className={styles.tableWrapper}>
              <h1 className={styles.subTitle}>{t("Common-PurchasedSkus")}</h1>

              <Table
                size="small"
                className={styles.table}
                loading={loadingPurchasedSkus}
                rowKey={(record) => record.skuId}
                columns={purchasedSkusColumns}
                dataSource={purchasedSkus}
                pagination={{
                  total: totalPurchasedSkus,
                  showSizeChanger: false,
                  onChange: handleFetchPurchasedItems,
                  current: actualPagePurchasedSkus,
                  pageSize: 5,
                }}
              />
            </div>

            <div className={styles.tableWrapper}>
              <h1 className={styles.subTitle}>{t("Common-PurchasedBrands")}</h1>

              <Table
                size="small"
                className={styles.table}
                loading={loadingPurchasedBrands}
                rowKey={(record) => record.brandId}
                columns={purchasedBrandsColumns}
                dataSource={purchasedBrands}
                pagination={{
                  total: totalPurchasedBrands,
                  showSizeChanger: false,
                  onChange: handleFetchPurchasedItems,
                  current: actualPagePurchasedBrands,
                  pageSize: 5,
                }}
              />
            </div>

            <div className={styles.tableWrapper}>
              <h1 className={styles.subTitle}>{t("Common-PurchasedCategories")}</h1>

              <Table
                size="small"
                className={styles.table}
                loading={loadingPurchasedCategories}
                rowKey={(record) => record.categoryId}
                columns={purchasedCategoriesColumns}
                dataSource={purchasedCategories}
                pagination={{
                  total: totalPurchasedCategories,
                  showSizeChanger: false,
                  onChange: handleFetchPurchasedItems,
                  current: actualPagePurchasedCategories,
                  pageSize: 5,
                }}
              />
            </div>
              </div>
            </TabPane>
            
            <TabPane tab={t("Common-Promotions")} key="promotions">
              <div className={styles.infoWrapper}>
                <h1 className={styles.subTitle}>{"Promociones"}</h1>
            <div className={styles.tableWrapper}>
              <h1 className={styles.subTitle}>{t("Common-PromotionsUsed")}</h1>

              <Table
                size="small"
                className={styles.table}
                loading={loadingPromotionsUsed}
                rowKey={(record) => record.id}
                columns={promotionsUsedColumns}
                dataSource={promotionsUsed}
                pagination={{
                  total: totalPromotionsUsed,
                  showSizeChanger: false,
                  onChange: handleFetchPromotionsUsed,
                  current: actualPagePromotionsUsed,
                }}
              />
            </div>
              </div>
            </TabPane>
            
            <TabPane tab={t("Common-Points")} key="points">
              <div className={styles.infoWrapper}>
                <div className={styles.headerTableWrapper}>
                  <h1 className={styles.subTitle}>{t("Common-AccumulateUnits")}</h1>
              <Button
                type="primary"
                className={styles.editButton}
                onClick={() => setIsEditUnitsModalVisible(true)}
                icon={<EditOutlined />}
              />
            </div>
            <div className={styles.statsWrapper}>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-Actives")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {client?.activePoints ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-FutureExpirations")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {client?.futureExpirations ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-Pendings")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {client?.pendingPoints ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-TotalGeneratedPoints")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {client?.totalGeneratedPoints ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-Spent")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {client?.spentPoints ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-PointsLimit")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {client?.pointsLimit ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-Expired")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {client?.expiredPoints ?? "-"}
                </Typography.Text>
              </div>
              <div className={styles.statItem}>
                <Typography.Text className={styles.label}>
                  {t("Common-PointsUntilLimit")}:
                </Typography.Text>
                <Typography.Text className={styles.value}>
                  {client?.pointsUntilLimit ?? "-"}
                </Typography.Text>
              </div>
            </div>

            {pointsSummary && pointsSummary.nextExpiration && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 16,
                  margin: "8px 0 16px",
                }}
              >
                <div
                  style={{
                    background: "#fff1f0",
                    border: "1px solid #ffccc7",
                    borderRadius: 8,
                    padding: "16px 20px",
                    minWidth: 240,
                  }}
                >
                  <Typography.Text style={{ color: "#cf1322", display: "block" }}>
                    {t("Common-NextPointsToExpire")}
                  </Typography.Text>
                  <Typography.Text
                    strong
                    style={{ fontSize: 24, color: "#cf1322", display: "block" }}
                  >
                    {pointsSummary.nextExpiration.points.toLocaleString("es-AR")}
                  </Typography.Text>
                  <Typography.Text style={{ color: "#cf1322" }}>
                    {t("Common-ExpiresOn")}:{" "}
                    {moment(pointsSummary.nextExpiration.expiresAt).format(
                      "DD/MM/YYYY"
                    )}{" "}
                    ({t("Common-InDays", {
                      count: pointsSummary.nextExpiration.daysUntilExpiration,
                    })})
                  </Typography.Text>
                </div>

                {pointsSummary.expiringSoon.length > 1 && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: 280,
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      padding: "12px 16px",
                    }}
                  >
                    <Typography.Text
                      strong
                      style={{ display: "block", marginBottom: 8 }}
                    >
                      {t("Common-UpcomingExpirations")}
                    </Typography.Text>
                    {pointsSummary.expiringSoon.slice(0, 5).map((bucket) => (
                      <div
                        key={bucket.expiresAt}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          borderBottom: "1px dashed #f0f0f0",
                        }}
                      >
                        <Typography.Text strong>
                          {bucket.points.toLocaleString("es-AR")}{" "}
                          {t("Common-Points").toLowerCase()}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          {moment(bucket.expiresAt).format("DD/MM/YYYY")} (
                          {t("Common-InDays", {
                            count: bucket.daysUntilExpiration,
                          })}
                          )
                        </Typography.Text>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className={styles.tableWrapper}>
              <div className={styles.headerTableWrapper}>
                <h1 className={styles.subTitle}>{t("Common-Movements")}</h1>
                <Button
                  type="primary"
                  className={styles.actionsButton}
                  onClick={() => setIsCreateMovementModalVisible(true)}
                  icon={<PlusOutlined />}
                />
              </div>
              <Table
                size="small"
                className={styles.table}
                loading={loadingMovements}
                rowKey={(record) => record.id}
                columns={movementsColumns}
                dataSource={clientMovements}
                pagination={{
                  total: totalSizeMovements,
                  showSizeChanger: false,
                  onChange: handleFetchClientMovements,
                  current: actualPageMovements,
                }}
              />
            </div>
              </div>
            </TabPane>
            
            <TabPane tab={t("Common-ClientDiagnosis")} key="diagnosis">
              <div className={styles.infoWrapper}>
                {id && <ViewClientDiagnosisTab clientId={id} />}
              </div>
            </TabPane>

            <TabPane tab={t("Common-Recommendation")} key="recommendations">
              <div className={styles.infoWrapper}>
                <div className={styles.tableWrapper}>
                  <div className={styles.headerTableWrapper}>
                    <h1 className={styles.subTitle}>
                      {"Recomendaciones de productos"}
                    </h1>
                <Button
                  type="primary"
                  className={styles.actionsButton}
                  // onClick={() => setIsCreateRecommendationModalVisible(true)}
                  icon={<PlusOutlined />}
                />
              </div>
              <Table
                size="small"
                className={styles.table}
                loading={loadingProductsRecommendations}
                rowKey={(record) => record.id}
                columns={productsRecommendationsColumns}
                dataSource={productsRecommendations}
                pagination={{
                  total: totalProductsRecommendations,
                  showSizeChanger: false,
                  onChange: handleFetchProductsRecommendations,
                  current: actualPageProductsRecommendations,
                }}
              />
            </div>
              </div>
            </TabPane>
            
            <TabPane tab={t("Common-Rewards")} key="rewards">
              <div className={styles.infoWrapper}>
                <h1 className={styles.subTitle}>{t("Common-Rewards")}</h1>
              </div>
        </TabPane>
      </Tabs>
      <Modal
        title={t("Common-PointsMovement")}
        visible={isCreateMovementModalVisible}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        onCancel={() => setIsCreateMovementModalVisible(false)}
        width={500}
        centered
      >
        <Form
          name="addMovement"
          initialValues={{ remember: true }}
          autoComplete="off"
          layout="vertical"
          form={addMovementForm}
          className={styles.modalForm}
        >
          <Form.Item
            className={styles.modalFormItem}
            label={t("Common-Type")}
            name="type"
            rules={[
              {
                required: true,
                message: t("Common-Attribute"),
              },
            ]}
          >
            <Select placeholder={t("Common-SelectType")}>
              <Select.Option value="ACCUMULATION">
                {t("Common-Accumulation")}
              </Select.Option>
              <Select.Option value="CONSUMPTION">
                {t("Common-Consumption")}
              </Select.Option>
              <Select.Option value="ADJUSTMENT">
                {t("Common-Adjustment")}
              </Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.modalFormItem}
            label={t("Common-Points")}
            name="pointsMoved"
            rules={[
              {
                required: true,
                message: t("Common-Points"),
              },
            ]}
          >
            <InputNumber 
              className={styles.inputNumber}
              placeholder={t("Common-EnterPoints")}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            className={styles.modalFormItem}
            label={t("Common-Comments")}
            name="comments"
            rules={[
              {
                required: true,
                message: t("Common-Comments"),
              },
            ]}
          >
            <Input placeholder={t("Common-EnterComments")} />
          </Form.Item>
          <Form.Item
            className={styles.modalFormItem}
            label={t("Common-ExpirationDateOptional")}
            name="expirationDate"
            extra={t("Common-ExpirationDateHint")}
          >
            <DatePicker
              format="DD/MM/YYYY"
              placeholder={t("Common-SelectDate")}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
        <div className={styles.modalButtonWrapper}>
          <Button
            className={styles.modalCancelButton}
            type="primary"
            onClick={() => setIsCreateMovementModalVisible(false)}
          >
            {t("Common-Cancel")}
          </Button>
          <Button
            className={styles.modalButton}
            type="primary"
            onClick={handleAddMovement}
          >
            {t("Common-Add")}
          </Button>
        </div>
      </Modal>
      <Modal
        title={t("Common-Recommendation")}
        visible={isCreateRecommendationVisible}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        onCancel={() => setIsCreateRecommendationModalVisible(false)}
      >
        <Form
          name="addRecommendation"
          initialValues={{ remember: true }}
          autoComplete="off"
          layout="vertical"
          form={addMovementForm}
        >
          <Form.Item
            className={styles.inputNumber}
            label={t("Common-BuyProbability")}
            name="buyProbability"
            rules={[
              {
                required: true,
                message: t("Common-BuyProbability"),
              },
            ]}
          >
            <InputNumber className={styles.inputNumber}></InputNumber>
          </Form.Item>
        </Form>
        <Button
          className={styles.addButton}
          type="primary"
          onClick={handleAddMovement}
        >
          {t("Common-Add")}
        </Button>
      </Modal>
      <Modal
        title={t("Common-Points")}
        visible={isEditUnitsModalVisible}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        onCancel={() => setIsEditUnitsModalVisible(false)}
        width={500}
        centered
      >
        <Form
          name="unitsForm"
          initialValues={{ remember: true }}
          autoComplete="off"
          layout="vertical"
          form={unitsForm}
          className={styles.modalForm}
        >
          <Form.Item
            className={styles.modalFormItem}
            label={t("Common-Actives")}
            initialValue={client?.activePoints}
            name="activePoints"
            rules={[
              {
                required: true,
                message: t("Common-Actives"),
              },
            ]}
          >
            <InputNumber 
              className={styles.inputNumber}
              placeholder={t("Common-EnterActivePoints")}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            className={styles.modalFormItem}
            initialValue={client?.pendingPoints}
            label={t("Common-Pendings")}
            name="pendingPoints"
            rules={[
              {
                required: true,
                message: t("Common-Pendings"),
              },
            ]}
          >
            <InputNumber 
              className={styles.inputNumber}
              placeholder={t("Common-EnterPendingPoints")}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
        <div className={styles.modalButtonWrapper}>
          <Button
            className={styles.modalCancelButton}
            type="primary"
            onClick={() => setIsEditUnitsModalVisible(false)}
          >
            {t("Common-Cancel")}
          </Button>
          <Button
            className={styles.modalButton}
            type="primary"
            onClick={handleEditUnits}
          >
            {t("Common-Save")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ViewClient;
